import { useCallback, useEffect, useState } from 'react';
import type { FC, MouseEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card } from '@material-ui/core';
import { FilterType } from 'src/enums/common';
import { FilterBar } from 'src/components/common/filter-bar';
import { MastersWrapper } from 'src/components/common/masters-wrapper';
import { CustomerAccountDeletionRequestsDetails } from 'src/interfaces/customers/customers';
import { accountDeletionRequestApi } from 'src/api/customerADApi';
import { CustomerRequestTable } from 'src/components/customers/deleteAccountRequests/table';
import type { FilterFormat, FilterProperties } from '../../types/filter';
import { useMounted } from '../../hooks/use-mounted';
import { ResponseCodes } from '../../enums/responseCodes';
import { PaginationAndFilter } from '../../interfaces/common/pagination';

interface CustomerRequestsState {
  data?: {
    requests: CustomerAccountDeletionRequestsDetails[];
    requestsCount: number;
  };
  error?: string;
  isLoading: boolean;
}

interface Controller {
  filters: Array<FilterFormat>;
  page: number;
  query: string;
  sort: 'asc' | 'desc';
  sortBy: string;
  view: string;
  pageSize?: number;
}

const dbPropertyNames = {
  statuses: 'status',
  customerNames: 'customerName',
  id: 'id',
};

export const DeleteCustomer: FC = () => {
  const mounted = useMounted();
  const [controller, setController] = useState<Controller>({
    filters: [],
    page: 0,
    query: '',
    sort: 'desc',
    sortBy: 'lastUpdate',
    view: 'all',
    pageSize: 10
  });
  const [CustomerRequestsState,
    setCustomerRequestsState] = useState<CustomerRequestsState>({ isLoading: true });
  const [filterData, setFilterData] = useState(null);
  const [filtersList, setFiltersList] = useState<FilterProperties[]>(null);
  const [filters, setFilters] = useState<FilterFormat[]>([]);

  // Fetch filter options data
  useEffect(() => {
    const fetchData = async () => {
      const data = accountDeletionRequestApi.getAccountDeletionRequestsFilterData();
      return data;
    };
    fetchData().then((result) => {
      setFilterData(result.data);
    }).catch((err) => {
      console.log(err);
    });
  }, []);

  // Fetch all requests data
  const getrequests = useCallback(async () => {
    setCustomerRequestsState(() => ({ isLoading: true }));

    try {
      const paginationObject: PaginationAndFilter = {
        filters: controller.filters,
        pageSize: controller.pageSize,
        pageNumber: controller.page + 1,
        sort: controller.sort,
        sortBy: controller.sortBy
      };
      // call to the pagination api
      const result = await accountDeletionRequestApi
        .getAccountDeletioRequestsByPaginationAndFilters(paginationObject);

      if (mounted.current) {
        if (result.statusCode === ResponseCodes.OK) {
          setCustomerRequestsState(() => ({
            isLoading: false,
            data: {
              requests: result.responseData.records,
              requestsCount: result.responseData.totalRecords
            }
          }));
        }
      }
    } catch (err) {
      console.error(err);

      if (mounted.current) {
        setCustomerRequestsState(() => ({
          isLoading: false,
          error: err.message
        }));
      }
    }
  }, [controller]);

  useEffect(() => {
    getrequests().catch(console.error);
  }, [controller, filters]);

  // Create filters object
  useEffect(() => {
    if (filterData) {
      const requestIdFilter: FilterProperties = {
        label: 'Request ID',
        dbProperty: dbPropertyNames.id,
        filterType: FilterType.TEXT,
      };

      const customerNameFilter: FilterProperties = {
        label: 'Customer Name',
        dbProperty: dbPropertyNames.customerNames,
        filterType: FilterType.TEXT,
        options: filterData.customers.map((entry) => ({
          id: entry,
          value: entry
        }))
      };

      const statusFilter: FilterProperties = {
        label: 'Status',
        dbProperty: dbPropertyNames.statuses,
        filterType: FilterType.MULTI_SELECT,
        options: filterData.statuses.map((entry) => ({
          id: entry,
          value: entry
        }))
      };

      const list: Array<FilterProperties> = [requestIdFilter,
        statusFilter,
        customerNameFilter];
      setFiltersList(list);
    }
  }, filterData);

  const handlePageChange = (newPage: number): void => {
    setController({
      ...controller,
      page: newPage - 1
    });
  };

  const handleSortChange = (event: MouseEvent<HTMLElement>, property: string): void => {
    const isAsc = controller.sortBy === property && controller.sort === 'asc';

    setController({
      ...controller,
      page: 0,
      sort: isAsc ? 'desc' : 'asc',
      sortBy: property
    });
  };

  const applyFiltersHandler = (newFilters: FilterFormat[]) => {
    setFilters(newFilters);
    setController((prevState) => ({
      ...prevState,
      filters: newFilters,
      page: 0,
      pageSize: 10
    }));
  };

  const handleCrud = (): void => {
    setController({
      ...controller
    });
  };

  return (
    <>
      <Helmet>
        <title>Delete Customer | ServCare</title>
      </Helmet>
      <MastersWrapper>
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1
          }}
        >
          {filtersList && (
          <FilterBar
            filters={filtersList}
            onApply={applyFiltersHandler}
          />
          )}
          <CustomerRequestTable
            error={CustomerRequestsState.error}
            isLoading={CustomerRequestsState.isLoading}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            page={controller.page + 1}
            requests={CustomerRequestsState.data?.requests}
            requestsCount={CustomerRequestsState.data?.requestsCount}
            sort={controller.sort}
            sortBy={controller.sortBy}
            onCrud={handleCrud}
          />
        </Card>
      </MastersWrapper>
    </>
  );
};
